type QueryMetric = {
  key: string;
  count: number;
  cacheHit: number;
  cacheMiss: number;
  totalDurationMs: number;
  totalPayloadBytes: number;
  lastDurationMs: number;
  lastPayloadBytes: number;
  lastUpdatedAt: string;
};

type RealtimeMetric = {
  source: string;
  event: string;
  table?: string;
  count: number;
  lastEntityId?: string;
  lastUpdatedAt: string;
};

type SupabaseCallMetric = {
  kind: "from" | "rpc";
  target: string;
  caller: string;
  count: number;
  errorCount: number;
  totalDurationMs: number;
  lastDurationMs: number;
  lastStatus?: number;
  lastError?: string;
  lastUpdatedAt: string;
  lastStack?: string[];
};

type RealtimeSubscriptionMetric = {
  channel: string;
  event: "created" | "subscribed" | "unsubscribed" | "removed";
  count: number;
  lastStatus?: string;
  lastUpdatedAt: string;
  lastStack?: string[];
};

const metrics = new Map<string, QueryMetric>();
const realtimeMetrics = new Map<string, RealtimeMetric>();
const supabaseCallMetrics = new Map<string, SupabaseCallMetric>();
const realtimeSubscriptionMetrics = new Map<string, RealtimeSubscriptionMetric>();
const lastStartedAt = new Map<string, number>();

function isDevLikeEnvironment() {
  const mode = String(import.meta.env.MODE ?? "").toLowerCase();
  return mode === "development" || mode === "dev" || mode === "test";
}

export function observeQueryStart(key: string) {
  if (!isDevLikeEnvironment()) return;
  const now = Date.now();
  const prev = lastStartedAt.get(key) ?? 0;
  lastStartedAt.set(key, now);
  if (prev && now - prev <= 1_000) {
    console.warn(`[QueryObservability] Duplicate call detected for "${key}" within 1s`);
  }
}

export function recordQueryMetric(input: {
  key: string;
  durationMs: number;
  payloadBytes: number;
  cacheHit: boolean;
}) {
  const existing = metrics.get(input.key);
  const next: QueryMetric = existing
    ? {
        ...existing,
        count: existing.count + 1,
        cacheHit: existing.cacheHit + (input.cacheHit ? 1 : 0),
        cacheMiss: existing.cacheMiss + (input.cacheHit ? 0 : 1),
        totalDurationMs: existing.totalDurationMs + input.durationMs,
        totalPayloadBytes: existing.totalPayloadBytes + input.payloadBytes,
        lastDurationMs: input.durationMs,
        lastPayloadBytes: input.payloadBytes,
        lastUpdatedAt: new Date().toISOString(),
      }
    : {
        key: input.key,
        count: 1,
        cacheHit: input.cacheHit ? 1 : 0,
        cacheMiss: input.cacheHit ? 0 : 1,
        totalDurationMs: input.durationMs,
        totalPayloadBytes: input.payloadBytes,
        lastDurationMs: input.durationMs,
        lastPayloadBytes: input.payloadBytes,
        lastUpdatedAt: new Date().toISOString(),
      };
  metrics.set(input.key, next);
  if (isDevLikeEnvironment() && typeof window !== "undefined") {
    (window as unknown as { __koredorQueryMetrics?: QueryMetric[] }).__koredorQueryMetrics =
      getQueryMetricsSnapshot();
  }
}

export function getQueryMetricsSnapshot(): QueryMetric[] {
  return Array.from(metrics.values()).sort((a, b) => a.key.localeCompare(b.key));
}

export function recordRealtimeMetric(input: {
  source: string;
  event: "patch" | "exact-refetch" | "full-refetch" | "invalidate" | "broadcast";
  table?: string;
  entityId?: string;
  detail?: Record<string, unknown>;
}) {
  if (!isDevLikeEnvironment()) return;
  const key = [input.source, input.event, input.table ?? "unknown"].join(":");
  const existing = realtimeMetrics.get(key);
  const next: RealtimeMetric = existing
    ? {
        ...existing,
        count: existing.count + 1,
        lastEntityId: input.entityId ?? existing.lastEntityId,
        lastUpdatedAt: new Date().toISOString(),
      }
    : {
        source: input.source,
        event: input.event,
        table: input.table,
        count: 1,
        lastEntityId: input.entityId,
        lastUpdatedAt: new Date().toISOString(),
      };
  realtimeMetrics.set(key, next);
  if (typeof window !== "undefined") {
    (window as unknown as { __koredorRealtimeMetrics?: RealtimeMetric[] }).__koredorRealtimeMetrics =
      getRealtimeMetricsSnapshot();
  }
  if (input.detail) {
    console.info("[RealtimeObservability]", input);
  }
}

export function getRealtimeMetricsSnapshot(): RealtimeMetric[] {
  return Array.from(realtimeMetrics.values()).sort((a, b) =>
    `${a.source}:${a.event}:${a.table ?? ""}`.localeCompare(`${b.source}:${b.event}:${b.table ?? ""}`),
  );
}

export function recordSupabaseCallMetric(input: {
  kind: "from" | "rpc";
  target: string;
  caller: string;
  durationMs: number;
  status?: number;
  error?: unknown;
  stack?: string[];
}) {
  if (!isDevLikeEnvironment()) return;
  const key = [input.kind, input.target, input.caller].join(":");
  const errorMessage =
    typeof input.error === "string"
      ? input.error
      : input.error && typeof input.error === "object" && "message" in input.error
        ? String((input.error as { message?: unknown }).message ?? "Unknown error")
        : undefined;
  const existing = supabaseCallMetrics.get(key);
  const next: SupabaseCallMetric = existing
    ? {
        ...existing,
        count: existing.count + 1,
        errorCount: existing.errorCount + (errorMessage ? 1 : 0),
        totalDurationMs: existing.totalDurationMs + input.durationMs,
        lastDurationMs: input.durationMs,
        lastStatus: input.status ?? existing.lastStatus,
        lastError: errorMessage,
        lastUpdatedAt: new Date().toISOString(),
        lastStack: input.stack ?? existing.lastStack,
      }
    : {
        kind: input.kind,
        target: input.target,
        caller: input.caller,
        count: 1,
        errorCount: errorMessage ? 1 : 0,
        totalDurationMs: input.durationMs,
        lastDurationMs: input.durationMs,
        lastStatus: input.status,
        lastError: errorMessage,
        lastUpdatedAt: new Date().toISOString(),
        lastStack: input.stack,
      };
  supabaseCallMetrics.set(key, next);
  if (typeof window !== "undefined") {
    (window as unknown as { __koredorSupabaseCallMetrics?: SupabaseCallMetric[] }).__koredorSupabaseCallMetrics =
      getSupabaseCallMetricsSnapshot();
  }
}

export function getSupabaseCallMetricsSnapshot(): SupabaseCallMetric[] {
  return Array.from(supabaseCallMetrics.values()).sort((a, b) =>
    `${a.kind}:${a.target}:${a.caller}`.localeCompare(`${b.kind}:${b.target}:${b.caller}`),
  );
}

export function recordRealtimeSubscriptionMetric(input: {
  channel: string;
  event: "created" | "subscribed" | "unsubscribed" | "removed";
  status?: string;
  stack?: string[];
}) {
  if (!isDevLikeEnvironment()) return;
  const key = [input.channel, input.event].join(":");
  const existing = realtimeSubscriptionMetrics.get(key);
  const next: RealtimeSubscriptionMetric = existing
    ? {
        ...existing,
        count: existing.count + 1,
        lastStatus: input.status ?? existing.lastStatus,
        lastUpdatedAt: new Date().toISOString(),
        lastStack: input.stack ?? existing.lastStack,
      }
    : {
        channel: input.channel,
        event: input.event,
        count: 1,
        lastStatus: input.status,
        lastUpdatedAt: new Date().toISOString(),
        lastStack: input.stack,
      };
  realtimeSubscriptionMetrics.set(key, next);
  if (typeof window !== "undefined") {
    (window as unknown as { __koredorRealtimeSubscriptionMetrics?: RealtimeSubscriptionMetric[] }).__koredorRealtimeSubscriptionMetrics =
      getRealtimeSubscriptionMetricsSnapshot();
  }
}

export function getRealtimeSubscriptionMetricsSnapshot(): RealtimeSubscriptionMetric[] {
  return Array.from(realtimeSubscriptionMetrics.values()).sort((a, b) =>
    `${a.channel}:${a.event}`.localeCompare(`${b.channel}:${b.event}`),
  );
}
